import React, { useState } from "react";
import { AdminHead } from "../../components/admin/AdminHead";
import SearchBox from "@carrier-io/air-react/patterns/SearchBox";
import Box from "@carrier-io/air-react/Box";
import Stack from "@carrier-io/air-react/Stack";
import Button from "@carrier-io/air-react/Button";
import {
  UserDataDisplayType,
  UserManagementTable,
} from "../../components/userManagement/UserManagementTable";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { DEFAULT_REGION } from "../../constants/regions";
import { useUsersSearch } from "../../hooks/useUsersSearch";
import { SearchUsersParams } from "../../components/searchUsersParams/SearchUsersParams";
import { useDebounce } from "use-debounce";
import {
  USER_SEARCH_LIMIT,
  USER_SEARCH_MIN_LENGTH,
} from "../../constants/user";
import { useNavigate } from "react-router-dom";
import { RoutesPage } from "../../constants/routes";
import { transform } from "../../utils/transform";
import { useGetUsers } from "../../hooks/useGetUsers";
import { useRemountKey } from "../../hooks/useRemountKey";

export const UserManagement = () => {
  const [searchValue, setSearchValue] = useState("");
  const [roles, setRoles] = useState<Array<string>>([]);
  const [groups, setGroups] = useState<Array<string>>([]);
  const [debouncedValue] = useDebounce(searchValue, 500);
  const { key, remount } = useRemountKey();
  const navigate = useNavigate();
  const region = DEFAULT_REGION;

  const isEnabledSearch =
    debouncedValue.length >= USER_SEARCH_MIN_LENGTH &&
    !roles.length &&
    !groups.length;

  const {
    data: searchUsersData,
    isLoading: isLoadingSearch,
    next: nextSearch,
    prev: prevSearch,
    isHasNextPage: isHasNextPageSearch,
    page: pageSearch,
  } = useUsersSearch<UserDataDisplayType>({
    enabled: isEnabledSearch,
    value: debouncedValue,
    region,
    limit: USER_SEARCH_LIMIT,
    transform,
  });

  const {
    data: getUsersData,
    isLoading: isLoadingGetUsers,
    next: nextGetUsers,
    prev: prevGetUsers,
    isHasNextPage: isHasNextPageGetUsers,
    page: pageGetUsers,
  } = useGetUsers<UserDataDisplayType>({
    roles,
    groups,
    key: roles.join(",") + groups.join(",") + region,
    limit: USER_SEARCH_LIMIT,
    transform,
  });

  const handleSetRole = (role: string) => {
    setSearchValue("");
    remount();
    setRoles([role]);
  };

  const handleSetGroup = (group: string) => {
    setSearchValue("");
    remount();
    setGroups([group]);
  };

  const handleSetSearch = (e: { text: string }) => {
    setRoles([]);
    setGroups([]);
    setSearchValue(e.text);
  };

  const isLoading = isEnabledSearch ? isLoadingSearch : isLoadingGetUsers;
  const data = isEnabledSearch ? searchUsersData : getUsersData;
  const next = isEnabledSearch ? nextSearch : nextGetUsers;
  const prev = isEnabledSearch ? prevSearch : prevGetUsers;
  const page = isEnabledSearch ? pageSearch : pageGetUsers;
  const isHasNextPage = isEnabledSearch
    ? isHasNextPageSearch
    : isHasNextPageGetUsers;

  const isActivePagination = Boolean(data?.length && !isLoading);

  return (
    <>
      <AdminHead
        title={"User Management Test"}
        description={
          "The table below contains a list of all users, their details, their roles, and their statuses. To add new users, click the invite new user button. "
        }
      />
      <div style={{ padding: "16px 32px" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ marginBottom: 2, gap: "16px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box>
              <SearchBox
                key={key}
                data-testid="search-container"
                defaultValue={searchValue}
                onChange={handleSetSearch}
                multiple={false}
                sx={{
                  minWidth: "292px",
                  width: "100%",
                  backgroundColor: "transparent",
                  "& .MuiInputBase-root": {
                    backgroundColor: "transparent !important",
                    border: "1px solid" + " standardInputLine !important",
                  },
                }}
              />
            </Box>
            <SearchUsersParams
              role={roles[0]}
              group={groups[0]}
              onRoleChange={handleSetRole}
              onGroupChange={handleSetGroup}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              data-testid="bulk-add-users"
              variant="outlined"
              onClick={() => navigate(RoutesPage.AddUsers)}
              sx={{
                fontSize: "16px !important",
              }}
            >
              Bulk Add Users
            </Button>
            <Button
              data-testid="invite-user"
              startIcon={<AddOutlinedIcon fontSize="medium" />}
              onClick={() => navigate(RoutesPage.CreateUser)}
              sx={{
                padding: "8px 24px",
                fontSize: "16px !important",
              }}
            >
              Invite New User
            </Button>
          </Box>
        </Stack>
        <UserManagementTable
          isLoading={isLoading}
          isActivePagination={isActivePagination}
          users={data || []}
          region={region}
          onPrev={prev}
          isPrevDisabled={
            isLoading || page === 0 || searchValue !== debouncedValue
          }
          onNext={next}
          isNextDisabled={
            isLoading || !isHasNextPage || searchValue !== debouncedValue
          }
        />
      </div>
    </>
  );
};
